import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DoCheck,
    ElementRef,
    HostBinding,
    HostListener,
    Input,
    OnDestroy,
    Optional,
    Renderer2,
    Self,
    ViewChild,
} from "@angular/core";
import { NgControl } from "@angular/forms";
import { SpsActionDescriptor } from "@spscommerce/ds-shared";
import { AttributePresenceInput, RxEventually } from "@spscommerce/ng-utils";
import { onNextTick } from "@spscommerce/utils";

import { SpsFormControlComponent } from "../forms/sps-form-control.abstract.component";
import { SpsOptionListComponent } from "../option-list/sps-option-list.component";

@Component({
    selector: "sps-select",
    templateUrl: "./sps-select.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SpsSelectComponent<T> extends SpsFormControlComponent<T> implements AfterViewInit, DoCheck, OnDestroy {
    static readonly displayName = "sps-select";
    static readonly props = {
        options: {
            type: "RxEventually<Iterable<T>> | ((filter?: string) => RxEventually<Iterable<T>>)",
            required: true
        },
        action: "SpsActionDescriptor",
        notClearable: "boolean",
        placeholder: "string",
        searchPlaceholder: "string",
        zeroState: "string",
        textKey: "string",
        captionKey: "string",
        searchDebounce: "number",
    };

    private static _instances = new Set<SpsSelectComponent<any>>();

    @Input() options:
        | RxEventually<Iterable<T>>
        | ((filter?: string) => RxEventually<Iterable<T>>) = [];
    @Input() action: SpsActionDescriptor;

    @Input() @AttributePresenceInput() notClearable;

    @Input() placeholder = "Select one…";
    @Input() searchPlaceholder = "Search…";
    @Input() zeroState: string;

    @Input() textKey: string;
    @Input() captionKey: string;
    @Input() searchDebounce: number;

    @HostBinding("class.sps-select") readonly spsSelectClass = true;

    @ViewChild("controlButton", { static: false }) private _controlButton: ElementRef;
    @ViewChild(SpsOptionListComponent, { static: false }) private _dropdown: SpsOptionListComponent<T>;

    search = "";

    @HostBinding("class.sps-select--clearable")
    get isClearable() {
        return !this.notClearable;
    }

    @HostBinding("class.sps-select--async")
    isAsync;

    /** Whether the dropdown is open */
    @HostBinding("class.sps-select--open")
    @HostBinding("class.z-stratum-dropdown")
    isOpen;

    /** Whether the dropdown goes up from the drop control button instead of down as usual */
    @HostBinding("class.sps-select--opens-upward")
    opensUpward;


    get value(): T {
        return this.model;
    }
    set value(newValue: T) {
        this.model = newValue;
    }

    get text() {
        return this.value && this.textKey ? this.value[this.textKey] : this.value;
    }

    constructor(
        protected _changeDetector: ChangeDetectorRef,
        _el: ElementRef,
        _renderer: Renderer2,
        @Self()
        @Optional()
        _ngControl: NgControl
    ) {
        super(_el, _renderer, _ngControl);

        if (SpsSelectComponent._instances.size === 0) {
            document.addEventListener("click", SpsSelectComponent._onDocumentClick);
        }

        SpsSelectComponent._instances.add(this);
    }

    static _onDocumentClick(event: MouseEvent) {
        for (const instance of SpsSelectComponent._instances) {
            if (!instance.isDisabled) {
                if (instance._controlButton.nativeElement.contains(event.target)) {
                    instance._dropdown.toggle();
                    instance._updateFocus();
                } else if (!instance._el.nativeElement.contains(event.target)) {
                    instance._dropdown.close();
                }
            }
        }
    }

    ngDoCheck() {
        this.isAsync = this._dropdown && this._dropdown.isAsync;
        this.isOpen = this._dropdown && this._dropdown.isOpen;
        this.opensUpward = this._dropdown && this._dropdown.opensUpward;
    }

    ngOnDestroy() {
        if (SpsSelectComponent._instances.has(this)) {
            SpsSelectComponent._instances.delete(this);

            if (SpsSelectComponent._instances.size === 0) {
                document.removeEventListener("click", SpsSelectComponent._onDocumentClick);
            }
        }
    }

    @HostListener("click", ["$event"])
    _onClick(event: MouseEvent) {
        event.preventDefault();
    }

    _onClearIconClick(event: MouseEvent) {
        event.stopPropagation();
        this.value = null;
        this._updateFocus();
    }

    selectOption(option: T) {
        this.value = option;
        this._dropdown.close();
        this._updateFocus();
    }

    @HostListener("keydown", ["$event"])
    _onKeyDown(event: KeyboardEvent): void {
        if (!this.isDisabled && this._dropdown) {
            this._dropdown._onKeyDown(event);
            this._updateFocus();
        }
    }

    private _updateFocus() {
        if (this.isAsync) {
            if (this.isOpen) {
                onNextTick(() => {
                    if (this._dropdown && this._dropdown.searchInput) {
                        this._dropdown.searchInput.nativeElement.focus();
                    }
                });
            } else {
                this._el.nativeElement.focus();
            }
        }
    }

    setDisabledState(isDisabled: boolean): void {
        super.setDisabledState(isDisabled);
        if (this.isDisabled) {
            this._dropdown.close();
        }
    }
}
